/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Styled } from './index.styles';
import Accordion from '../Accordion';
import { ComponentProps, TransportationProps, RestrictedCountryListItemProps } from '../HotelOverview.types';
import { clickTrackingLoc } from '../constants/ApplicationConstants';
import { useConditionalPageContextSERP, useConditionalServerDataHotelOverviewSERP } from '../../../hooks';
import { overviewMockData } from '../mockdata/overview';
import clsx from 'clsx';
import { useCheckBreakpoint } from '../../../hooks';
import { getBreakpoint } from '../../../modules/utils/helpers';
import { useHQVContext } from '../../../context/HQVContext';
import { MapComponent } from '../MapComponent';
import { useSearchResultsStore } from '../../../modules/store/searchResultsStore';
import { formatPhoneNumber } from '../../../utils/src/utils';
import { logger } from '../../../utils';
declare global {
  interface Window {
    initMap: any;
    // google: any;
  }
}

const { log } = logger({})('HotelOverview/Location');

export const Location: React.FC<ComponentProps> = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { jsonContent }: any = useConditionalServerDataHotelOverviewSERP();
  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');
  const { hqvState } = useHQVContext();

  const { pageState, currentLocale, sessionData, isAuthorMode, pageVal } = useConditionalPageContextSERP();
  const isFlexibleDateSearch = sessionData?.cacheData?.data?.AriesCommon?.search_isFlexibleDate === 'true';

  const [_breakpoint, setBreakpoint] = useState<string | undefined>(getBreakpoint());

  // Throttle utility for resize handler
  const throttle = useCallback((func: () => void, limit: number) => {
    let inThrottle = false;
    return () => {
      if (!inThrottle) {
        func();
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }, []);

  // Reset breakpoint on window resize (throttled)
  useEffect(() => {
    const handleResize = throttle(() => {
      log.debug('resize, ', getBreakpoint());
      setBreakpoint(getBreakpoint());
    }, 150);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [throttle]);

  const locationDataGraph = useSearchResultsStore((state: { propertyInfoData: any }) => state.propertyInfoData);

  const locationData = useMemo(() => {
    return pageState?.authorMode || isAuthorMode
      ? overviewMockData.locationContentData
      : locationDataGraph?.propertyInfoData;
  }, [pageState, locationDataGraph]);

  const airportDetails = locationData?.property?.airports;
  const otherTransportationData = locationData?.property?.otherTransportation;
  const busTravel: TransportationProps[] = useMemo(() => {
    return otherTransportationData?.filter(
      (transportationForm: TransportationProps) => transportationForm?.type?.code?.indexOf('Bus') >= 0
    );
  }, [otherTransportationData]);
  const carRentals: TransportationProps[] = useMemo(() => {
    return otherTransportationData?.filter(
      (transportationForm: TransportationProps) => transportationForm?.type?.code?.indexOf('Car') >= 0
    );
  }, [otherTransportationData]);
  const subwayTravel: TransportationProps[] = useMemo(() => {
    return otherTransportationData?.filter(
      (transportationForm: TransportationProps) => transportationForm?.type?.code?.indexOf('Subway') >= 0
    );
  }, [otherTransportationData]);
  const trainTravel: TransportationProps[] = useMemo(() => {
    return otherTransportationData?.filter(
      (transportationForm: TransportationProps) => transportationForm?.type?.code?.indexOf('Train') >= 0
    );
  }, [otherTransportationData]);

  const basicInformation = locationData?.property?.basicInformation;
  const contactInformation = locationData?.property?.contactInformation;

  const addressObj = contactInformation?.address;
  const addressLine1 = addressObj?.line1 ?? '';
  const addressCity = addressObj?.city ?? '';
  const addressStateProvince = addressObj?.stateProvince?.description ?? '';
  const addressPostalCode = addressObj?.postalCode ?? '';
  let addressCountry = addressObj?.country?.description ?? '';
  const restrictedCountryList: RestrictedCountryListItemProps[] = jsonContent?.countryListRestricted ?? [];
  const addressCountryCode = addressObj?.country?.code;
  // Reading through list of countries to be hidden and replacing the country with the new country
  restrictedCountryList?.forEach(restrictedCountryItem => {
    if (restrictedCountryItem?.countryItemRestricted === addressCountryCode) {
      addressCountry = restrictedCountryItem?.replacedCountryItem;
    }
  });
  if (
    jsonContent?.overridePropertyCountry &&
    addressObj?.country?.description === jsonContent?.overridePropertyCountryText
  ) {
    addressCountry = '';
  }
  const [isActive, setIsActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Function to handle click of the location component accordians
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const accordionClick = (event: any, value: number): void => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      event.preventDefault();
      if (value === activeIndex) {
        setActiveIndex(-1);
      } else {
        setActiveIndex(value);
      }
      setIsActive(false);
    }
  };

  // function to determine if a comma should be displayed or not
  function addressDelimiterFinder(startString: string, addressArr: string[]) {
    const filteredAddressArr = addressArr.filter(str => str !== '');
    if (startString && filteredAddressArr.length > 0) {
      return ', ';
    }
    return ' ';
  }

  return (
    <Styled>
      {locationData && (
        <div
          id="hqv-location-section"
          className={clsx(
            pageVal === 'chat' && !isViewportL ? 'm-0' : '',
            'color-scheme1',
            'location-box',
            'adjustable-margin-element',
            `${hqvState?.modalOpenState ? 'hqv-modal' : ''}`
          )}
        >
          <div className={clsx('location-box__container standard', pageVal === 'chat' && !isViewportL ? 'mt-3' : '')}>
            <div className="location-box__col1">
              <h2
                className={clsx(
                  pageVal === 'chat' ? 't-subtitle-m mb-4' : isViewportL ? 't-title-s' : 't-subtitle-xl',
                  'location-heading'
                )}
              >
                {jsonContent?.locationLabel}
              </h2>
              <div className={clsx('location-box__col2-mobile', pageVal === 'chat' && !isViewportL ? 'mt-0' : '')}>
                <div className="mobile-map">
                  <div id="location-box__map-box-mobile">
                    <MapComponent
                      currentLocale={currentLocale}
                      basicInformation={basicInformation}
                      isFlexibleDateSearch={isFlexibleDateSearch}
                      brandIconLabel={jsonContent?.brandIconLabel}
                    />
                  </div>
                </div>
              </div>
              <div className={clsx(pageVal === 'chat' ? 't-font-s' : '', 'location-box__desc')}>
                <div className="hotel-address">
                  <div className="hotel-address-line1">
                    {/* Not using the addressDelimiterFinder since the comma after addressLine1 follows different logic
                    According to FSD-91318, this is what is expected:

                      line1 city, state, country, postal code
                      If there is no country then line1 city, state, postal code
                      if no state then line1 city, country, postal code
                      If no city & country then line1, state, postal code
                      If no postal code , city & country then line1, city */}
                    {addressLine1 +
                      (addressLine1 &&
                      ((addressCity && !addressStateProvince && !addressCountry && !addressPostalCode) ||
                        (!addressCity && addressStateProvince && !addressCountry && addressPostalCode))
                        ? ', '
                        : ' ')}
                  </div>
                  <div className="hotel-address-city-postal">
                    {addressCity +
                      addressDelimiterFinder(addressCity, [addressStateProvince, addressPostalCode, addressCountry])}
                    {addressStateProvince +
                      addressDelimiterFinder(addressStateProvince, [addressPostalCode, addressCountry])}
                    {addressCountry + addressDelimiterFinder(addressCountry, [addressPostalCode])}
                    {addressPostalCode}
                  </div>
                </div>
              </div>
              {contactInformation?.contactNumbers[0]?.phoneNumber?.display &&
                contactInformation?.contactNumbers[0]?.phoneNumber?.original && (
                  <div className={clsx(pageVal === 'chat' ? 't-font-s' : '', 'location-box__cn-box')}>
                    <span className={clsx(isViewportM ? 't-subtitle-m' : 't-label-alt-xs', 'telephone-label')}>
                      {jsonContent?.telephoneLabel}:{' '}
                    </span>
                    <a
                      href={`tel:` + contactInformation?.contactNumbers[0]?.phoneNumber?.original}
                      className={clsx(
                        isViewportM ? 't-subtitle-m' : 't-label-alt-xs',
                        'telephone-link',
                        'custom_click_track'
                      )}
                      {...{ custom_click_track_value: `${clickTrackingLoc}| Location Telephone |internal` }}
                    >
                      <span dir="ltr" className="location-box__contactNumber">
                        {formatPhoneNumber(contactInformation?.contactNumbers[0]?.phoneNumber?.display, currentLocale)}
                      </span>
                    </a>
                  </div>
                )}
              <div className="location-box__accordian-container">
                <div
                  className={clsx(pageVal === 'chat' ? 't-font-s' : '', 'col-12', 'p-0', 'accordion', 'm-accordion')}
                  id="accordionExample1"
                >
                  {airportDetails?.map((airport: TransportationProps, index: number) => (
                    <Accordion
                      key={index}
                      accIndex={index}
                      active={index === activeIndex}
                      accordionClick={accordionClick}
                      aeroData={airport}
                    />
                  ))}
                  {(busTravel?.length > 0 ||
                    carRentals?.length > 0 ||
                    subwayTravel?.length > 0 ||
                    trainTravel?.length > 0) && (
                    <div className="accordion-item">
                      <h2 className="accordion-header location-box-header" id="otherTransportationHeading">
                        <button
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#otherTransporation1"
                          aria-expanded={isActive ? 'true' : 'false'}
                          aria-controls="otherTransporation1"
                          tabIndex={0}
                          className={clsx(
                            'accordion-button',
                            'location-box__accordian',
                            'other-transportation',
                            'custom_click_track',
                            isActive ? '' : 'collapsed'
                          )}
                          onClick={(event): void => {
                            if (event.type === 'click') {
                              setActiveIndex(-1);
                              setIsActive(!isActive);
                              event.preventDefault();
                            }
                          }}
                          onKeyDown={(event): void => {
                            if (event.key === 'Enter' || event.keyCode === 13) {
                              setActiveIndex(-1);
                              setIsActive(!isActive);
                              event.preventDefault();
                            }
                          }}
                          {...{
                            custom_click_track_value: `${clickTrackingLoc}| Other Transportation Accordian |internal`,
                          }}
                        >
                          <div className={clsx('m-accordion-title', 'airplane-icon-name', 'accordion-element')}>
                            <span
                              className={clsx(
                                'icon-s',
                                'mr-2',
                                'icon-shuttle',
                                pageVal === 'chat' ? '' : 'icon-decorative'
                              )}
                            ></span>
                            <span
                              className={clsx(
                                pageVal === 'chat' ? 't-font-s' : 't-font-m',
                                'location-box__location-name'
                              )}
                            >
                              <p>{jsonContent?.otherTransportationLabel}</p>
                            </span>
                            <span
                              className={clsx(
                                'accordion-arrow-icon',
                                isActive ? 'ml-3 icon-dropdown-up' : 'ml-3 icon-dropdown-down'
                              )}
                            ></span>
                          </div>
                        </button>
                      </h2>

                      {isActive && (
                        <div
                          className={clsx(
                            'color-scheme1',
                            'accordion-collapse',
                            'collapse',
                            'panel',
                            'accordion-content',
                            isActive ? ' show' : ''
                          )}
                          id="otherTransporation1"
                          aria-label="otherTransportationHeading"
                          role="region"
                          data-bs-parent="#accordionExample1"
                        >
                          {busTravel.length > 0 && (
                            <div className={clsx('bus-stations', 'new-travel-method', 'standard')}>
                              <p className="t-subtitle-m">{jsonContent?.busStationLabel}</p>
                              {busTravel.map((busStop: TransportationProps) => (
                                <div className={clsx('t-font-s', 'busStop', 'individual-item')}>
                                  <p>{busStop.name}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {carRentals.length > 0 && (
                            <div className={clsx('car-rentals', 'new-travel-method', 'standard')}>
                              <p className="t-subtitle-m">{jsonContent?.carRentalsLabel}</p>
                              {carRentals.map((rental: TransportationProps) => (
                                <>
                                  <div className={clsx('t-font-s', 'rental', 'individual-item')}>
                                    <p>{rental.name}</p>
                                  </div>
                                  <div className={clsx('individual-item', 'car-phone-number')}>
                                    {rental?.contactInformation?.phones[0] && (
                                      <a href={`tel:` + rental?.contactInformation?.phones[0].replace(/ /g, '')}>
                                        <span>{jsonContent?.phoneLabel}:&nbsp;</span>
                                        <span dir="ltr" className="phone-number">
                                          {formatPhoneNumber(
                                            rental?.contactInformation?.phones[0] ?? '',
                                            currentLocale
                                          )}
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                </>
                              ))}
                            </div>
                          )}
                          {subwayTravel.length > 0 && (
                            <div className={clsx('subway-stations', 'new-travel-method', 'standard')}>
                              <p className="t-subtitle-m">{jsonContent?.subwayStationLabel}</p>
                              {subwayTravel.map((subway: TransportationProps) => (
                                <div className={clsx('t-font-s', 'subway', 'individual-item')}>
                                  <p>{subway.name}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {trainTravel.length > 0 && (
                            <div className={clsx('train-stations', 'new-travel-method', 'standard')}>
                              <p className="t-subtitle-m">{jsonContent?.trainStationLabel}</p>
                              {trainTravel.map((train: TransportationProps) => (
                                <div className={clsx('t-font-s', 'train', 'individual-item')}>
                                  <p>{train.name}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="location-box__col2">
              <div className="desktop-map">
                <div id="location-box__map-box-desktop">
                  <MapComponent
                    currentLocale={currentLocale}
                    basicInformation={basicInformation}
                    isFlexibleDateSearch={isFlexibleDateSearch}
                    brandIconLabel={jsonContent?.brandIconLabel}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Styled>
  );
};

export default Location;
